import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import OfiPDFTemplate from "../../../components/PDF/Reporting/ofi/OfiPDFTemplate";
import ReportingFormDrawerV2 from "../../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamItemsSummary,
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../../hooks/useReportStream";
import {
	ReportItem,
	ReportStreamAssets,
} from "../../../hooks/useReportStreamFilter";
import {
	useReportPhases,
	useReportStreamNames,
	useTenantName,
} from "../../../hooks/useReportStreamLookups";
import { fetchReportingHubReports } from "../../../services/reporting-hub/fetchReports";
import { useTenantStore } from "../../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import { createOFILookupConfig } from "../../../utils/reporting/lookupConfigs/ofi";
import {
	collectOfiAuthorEmails,
	collectOfiExportEmails,
	getOfiAuthorName,
	getOfiExportData,
	getOfiSearchTexts,
	isAnonymousOfi,
	StreamOfi,
} from "../../../utils/reporting/ofiStream";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import {
	getDateValue,
	getItemsSummary,
	getReportNumberValue,
	getReportPlaceParts,
	StreamPlaces,
} from "../../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import {
	fetchExportNames,
	getReportExportData,
	lookUpReportForExport,
} from "../../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../../utils/reports";

type OfiRow = ReportItem &
	StreamOfi & {
		submitted_on?: string;
		is_rejected?: boolean;
		follow_up_actions?: unknown;
	};

// OFIs never reach Approved - Open, so they have four figures.
const OFI_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"closed",
];

const OFI_STREAM: ReportStreamConfig<OfiRow> = {
	statuses: OFI_STATUSES,
	defaultStatuses: OFI_STATUSES.filter((key) =>
		DEFAULT_REPORT_STATUSES.includes(key)
	),
	hasInternalExternal: false,
	filters: [],
	sortValues: {
		number: (report) => getReportNumberValue(report.report_number),
		date: (report) => getDateValue(report.submitted_on),
	},
};

const NO_OFIS: OfiRow[] = [];

const getOfiPath = (report: OfiRow) => `/reporting/ofi-stream/${report._id}`;

// The OFI's PDF, as its page exports it: the page's lookups for this OFI
// alone and the names of everyone it names, but never an anonymous OFI's
// author.
async function exportOfi(report: OfiRow) {
	const operators = report.operator ? [report.operator] : [];
	const [lookedUp, emailToName] = await Promise.all([
		lookUpReportForExport(report, createOFILookupConfig(operators)),
		fetchExportNames(collectOfiExportEmails(report)),
	]);
	await handleExportReport(
		getOfiExportData(
			getReportExportData(lookedUp, emailToName, "follow_up_actions")
		),
		"ofi",
		OfiPDFTemplate
	);
}

function OfiStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// The Reporting Hub fills and reads the same entry, and a saved report
	// is written into it (ReportingFormDrawerV2), so the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "ofi"],
		queryFn: () =>
			fetchReportingHubReports("ofi", currentTenantKey, isMasterTenant),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const ofis: OfiRow[] | undefined = Array.isArray(data) ? data : undefined;
	const rows = ofis ?? NO_OFIS;

	// Only the listed authors who aren't anonymous are looked up by name, and
	// only the listed OFIs' phases.
	const authorEmails = useMemo(() => collectOfiAuthorEmails(rows), [rows]);
	const authorNames = useReportStreamNames(
		["ofi-stream-authors"],
		authorEmails
	);
	const phaseMap = useReportPhases(rows, currentTenantKey);

	const getSearchTexts = useCallback(
		(report: OfiRow, assets: ReportStreamAssets) =>
			getOfiSearchTexts(
				report,
				{ ...assets, phaseMap },
				authorNames,
				getTenantName
			),
		[phaseMap, authorNames, getTenantName]
	);

	const view = useReportStream(
		ofis,
		OFI_STREAM,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);
	const places = useMemo<StreamPlaces>(
		() => ({ ...view.assets, phaseMap }),
		[view.assets, phaseMap]
	);

	const today = dayjs();
	const columns: ReportStreamColumn<OfiRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (report) => (
				<ReportStreamNumberCell
					to={getOfiPath(report)}
					reportNumber={report.report_number}
					operator={view.hasSeveralOperators ? report.operator : undefined}
				/>
			),
		},
		{
			key: "ofi",
			header: "OFI",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (report) => (
				<>
					<ReportStreamTitle title={report.title} />
					<ReportStreamMeta parts={getReportPlaceParts(report, places)} />
				</>
			),
		},
		{
			key: "date",
			header: "Submitted",
			width: "104px",
			narrow: "detail",
			sortKey: "date",
			render: (report) => formatDateDdMmmYyyy(report.submitted_on),
		},
		{
			key: "author",
			header: "Author",
			width: "140px",
			narrow: "detail",
			render: (report) => (
				<span
					className={
						isAnonymousOfi(report)
							? "report-stream__ellipsis report-stream__anonymous"
							: "report-stream__ellipsis"
					}
				>
					{getOfiAuthorName(report, authorNames)}
				</span>
			),
		},
		{
			key: "actions",
			header: "Actions",
			width: "148px",
			narrow: "full",
			render: (report) => (
				<ReportStreamItemsSummary
					kind={REPORT_ITEM_KINDS.follow_up_actions}
					summary={getItemsSummary(report.follow_up_actions, report, today)}
				/>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "132px",
			narrow: "status",
			render: (report) => (
				<ReportStreamStatusChip
					status={report.status}
					openStatus={report.open_status}
					isRejected={report.is_rejected === true}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Opportunities for improvement"
				subtitle={
					isMasterTenant ? "OFIs from every tenant." : "Your tenant's OFIs."
				}
				noun="OFI"
				plural="OFIs"
				newLabel="New OFI"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "OFIs from every tenant will show here."
						: "OFIs your tenant submits will show here."
				}
				searchPlaceholder="Search number, title, description, location, asset"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !ofis}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(report) => report._id}
				renderRowMenu={(report) => (
					<ReportStreamRowMenu
						reportNumber={report.report_number}
						path={getOfiPath(report)}
						tenantKey={currentTenantKey}
						onExport={() => exportOfi(report)}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="ofi"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default OfiStream;
